import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, PLATFORM_ID, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { EYesNo } from '../../../../../shared/models/types.enum';
import { FormUtils } from '../../../../../shared/helpers/formUtils';
import { DateUtils } from '../../../../../shared/helpers/dateUtils';

@Component({
  selector: 'threat-background',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './threat-background.component.html',
  styleUrl: './threat-background.component.css'
})
export class ThreatBackgroundComponent implements OnInit, AfterViewInit, OnChanges {
  
  @Input() fullTeamAssessmentData: any;
  @Output() onSaveSection2 = new EventEmitter<any>();

  @ViewChild('datepickerIncidentDate') datepickerIncidentDate!: ElementRef;
  yesNo = EYesNo;
  backgroundMotivationForm: FormGroup = new FormGroup([]);
  questionList = [
    {
      question: "Describe in detail the situation that prompted this Behavioral Threat Assessment: *",
      formControlName: "detail_of_situation",
    },
    {
      question: "Who was present during the situation? Please record full name(s) and contact information. *",
      formControlName: "situation_presence",
    }
  ]

  radioQuestions = [
    {
      id: 'specific_target_identified',
      questionText: 'Was there/were there specific target(s) identified? *',
      formControlName: 'specific_target_identified',
      infoFormControlName: 'specific_target_identified_information',
      label: 'Please identify the target(s): *'
    }
  ];

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private formUtils: FormUtils
  ) {}

  ngOnInit() {
    this.initializeForm();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fullTeamAssessmentData'] && this.fullTeamAssessmentData) {
      this.initializeForm();
    }
  }

  private initializeForm() {
    const group: any = {};
    
    this.radioQuestions.forEach(question => {
      group[question.formControlName] = new FormControl(String(this.fullTeamAssessmentData?.[question.formControlName]) || this.yesNo.No, Validators.required);
      group[question.infoFormControlName] = new FormControl({ value: this.fullTeamAssessmentData?.[question.infoFormControlName] || null, disabled: this.fullTeamAssessmentData?.[question.infoFormControlName] ? false : true });
    });
    this.questionList.forEach(question => {
      group[question.formControlName] = new FormControl(this.fullTeamAssessmentData?.[question.formControlName] || null, Validators.required);
    });
    group['date_of_incident'] = new FormControl(this.fullTeamAssessmentData?.date_of_incident || DateUtils.formatDateForBackend(DateUtils.getCurrentFormattedDate()), Validators.required);
    group['time_of_incident'] = new FormControl(this.fullTeamAssessmentData?.time_of_incident ||  '11:00', Validators.required);
    
    this.backgroundMotivationForm = new FormGroup(group);
    this.setupFormChangeHandlers();
  }

  async ngAfterViewInit(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      // Dynamically import the Datepicker to avoid server-side loading
      const { Datepicker } = await import('flowbite-datepicker');
      if (this.datepickerIncidentDate) {
        new Datepicker(this.datepickerIncidentDate.nativeElement, {
          autohide: true,
          format: 'mm/dd/yyyy'
        });
        // set default value to display for incident date
        if (this.fullTeamAssessmentData?.date_of_incident) {
          // if date_of_incident is available, set it to the datepicker
          this.datepickerIncidentDate.nativeElement.value = DateUtils.formatDateForDisplay(this.fullTeamAssessmentData.date_of_incident);
        } else {
          // if date_of_incident is not available, set it to the current date
          this.datepickerIncidentDate.nativeElement.value = DateUtils.getCurrentFormattedDate();
        }
        this.datepickerIncidentDate.nativeElement.addEventListener('changeDate', (event: any) => {
          const formattedDate = DateUtils.formatDateForBackend(event.target.value);
          this.backgroundMotivationForm.get('date_of_incident')?.setValue(formattedDate);
        });
      }
    }
  }

  setupFormChangeHandlers() {
    this.radioQuestions.forEach(question => {
      const control = this.backgroundMotivationForm.get(question.formControlName);
      if (control) {
        control.valueChanges.subscribe(value => {
          const infoControl = this.backgroundMotivationForm.get(question.infoFormControlName);
          if (infoControl) {
            this.formUtils.handleConditionChange(infoControl, value === this.yesNo.Yes);
          }
        });
      }
    });
  }

  saveBackgroundMotivation(): void {
    if (this.backgroundMotivationForm.valid) {
        this.onSaveSection2.emit(this.backgroundMotivationForm.value);
    }
  }

}
